import React, { useState } from 'react';
import { Channel } from '@/types';

interface FilterState {
  channels: Channel[];
  scoreRange: [number, number];
  daysRange: [number, number];
  sources: string[];
  intents: string[];
  states: string[];
}

interface PipelineFiltersProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onFiltersChange: (filters: FilterState) => void;
}

export const PipelineFilters: React.FC<PipelineFiltersProps> = ({
  isCollapsed,
  onToggle,
  onFiltersChange
}) => {
  const [filters, setFilters] = useState<FilterState>({
    channels: ['RETAIL', 'SL'],
    scoreRange: [0, 100],
    daysRange: [7, 30],
    sources: ['Website', 'Referral', 'Social Media'],
    intents: ['Purchase', 'Refinance', 'Cash-out'],
    states: ['CA', 'TX'],
  });

  const quickFilters = [
    { label: 'High Score', action: () => updateFilters({ scoreRange: [80, 100] }) },
    { label: 'Stalled >7d', action: () => updateFilters({ daysRange: [7, 30] }) },
    { label: 'Ready for Handoff', action: () => {} },
    { label: 'Recently Active', action: () => updateFilters({ daysRange: [0, 3] }) },
    { label: 'No Email Opens', action: () => {} },
    { label: 'SMS Only', action: () => {} },
  ];

  const updateFilters = (updates: Partial<FilterState>) => {
    const newFilters = { ...filters, ...updates };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  if (isCollapsed) {
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-lg p-3">
        <button
          onClick={onToggle}
          className="flex items-center space-x-2 text-slate-300 hover:text-white w-full"
        >
          <span>🔍</span>
          <span className="text-sm">Filters</span>
          <span className="ml-auto">▼</span>
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span>🔍</span>
          <span className="text-sm font-medium text-slate-300">Filters</span>
        </div>
        <button
          onClick={onToggle}
          className="text-slate-400 hover:text-white"
        >
          ▲
        </button>
      </div>

      {/* Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
        {/* Channel Filter */}
        <div>
          <label className="text-xs text-slate-400 mb-2 block">Channel:</label>
          <div className="flex space-x-2">
            {(['RETAIL', 'SL'] as Channel[]).map((channel) => (
              <label key={channel} className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  checked={filters.channels.includes(channel)}
                  onChange={(e) => {
                    const newChannels = e.target.checked
                      ? [...filters.channels, channel]
                      : filters.channels.filter(c => c !== channel);
                    updateFilters({ channels: newChannels });
                  }}
                  className="rounded"
                />
                <span className="text-xs text-slate-300">
                  {channel === 'RETAIL' ? 'AFN Retail' : 'SuperLoans'} ✓
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Score Range */}
        <div>
          <label className="text-xs text-slate-400 mb-2 block">Score:</label>
          <div className="flex items-center space-x-2">
            <input
              type="range"
              min="0"
              max="100"
              value={filters.scoreRange[1]}
              onChange={(e) => updateFilters({ 
                scoreRange: [filters.scoreRange[0], parseInt(e.target.value)] 
              })}
              className="flex-1"
            />
            <span className="text-xs text-slate-300 w-8">
              {filters.scoreRange[1]}
            </span>
          </div>
        </div>

        {/* Source Filter */}
        <div>
          <label className="text-xs text-slate-400 mb-2 block">Source:</label>
          <div className="flex flex-wrap gap-1">
            {['Website', 'Referral', 'Social'].map((source) => (
              <span 
                key={source}
                className="px-2 py-1 bg-slate-700 text-xs rounded"
              >
                {source}
              </span>
            ))}
          </div>
        </div>

        {/* Days Range */}
        <div>
          <label className="text-xs text-slate-400 mb-2 block">Days:</label>
          <div className="flex items-center space-x-2">
            <input
              type="range"
              min="0"
              max="30"
              value={filters.daysRange[1]}
              onChange={(e) => updateFilters({ 
                daysRange: [filters.daysRange[0], parseInt(e.target.value)] 
              })}
              className="flex-1"
            />
            <span className="text-xs text-slate-300 w-8">
              {filters.daysRange[1]}
            </span>
          </div>
        </div>

        {/* Intent Filter */}
        <div>
          <label className="text-xs text-slate-400 mb-2 block">Intent:</label>
          <div className="flex flex-wrap gap-1">
            {['Purchase', 'Refinance', 'Cash-out'].map((intent) => (
              <span 
                key={intent}
                className="px-2 py-1 bg-slate-700 text-xs rounded"
              >
                {intent}
              </span>
            ))}
          </div>
        </div>

        {/* State Filter */}
        <div>
          <label className="text-xs text-slate-400 mb-2 block">State:</label>
          <div className="flex flex-wrap gap-1">
            {['CA', 'TX'].map((state) => (
              <span 
                key={state}
                className="px-2 py-1 bg-slate-700 text-xs rounded"
              >
                {state} ✓
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Filters */}
      <div>
        <label className="text-xs text-slate-400 mb-2 block">Quick Filters:</label>
        <div className="flex flex-wrap gap-2">
          {quickFilters.map((filter) => (
            <button
              key={filter.label}
              onClick={filter.action}
              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-500"
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};